import type { Race } from "@sins2-atlas/game-data";
import { clsx } from "clsx";
import { useEffect, useMemo, useRef } from "react";
import { priceParts } from "../lib/format";
import {
  deselectAllTech,
  type StatFrame,
  selectAllTech,
  type TechMark,
  techMarker,
  toggleTech,
} from "../lib/frame";
import { FULL_SET_NOTE } from "../lib/tech-detail";
import { researchIndex, type TechOption, techGroupsFor, techKeyLabel } from "../lib/tech-index";
import { EMPTY_TECH_SET, impliers, type TechKey } from "../lib/tech-set";
import { RACE_META } from "../lib/vocabulary";
import { ArtImage } from "./art-image";
import { Chip } from "./chip";
import { keepPanelOnScreen, PanelAction, PickerPanel, RowFooter } from "./picker-panel";

// The Custom picker (Issue 25, ADR 0009): the panel a reader ticks a Tech Set in. One section
// per Race on the page, and within a Race the game's own shape (ADR 0005) — Domain, then Tier
// I to V, the Tier's own unlock row before the subjects that sit in it, subjects in tree
// order. Every row carries what the game's tree tooltip carries for it: the art, the name, the
// Effect Lines (a Tier's are its Tier Bonuses), the Price, and the Faction short name where
// only one Faction of the Race can research it. The words and numbers are all the game's; the
// panel adds none of its own.
//
// Whether the panel is open is transient (the search palette's rule, ADR 0002): it belongs to
// this visit, never to the link. What is ticked lives in `?tech=` through onChange.
//
// Ticking goes through frame.toggleTech, so the set stays one the game could reach: a tick
// pulls in the prerequisite chain and the Tier unlocks it needs, and an untick takes what
// depended on it. That gives every row one of four states — ticked (the reader ticked it),
// implied (another tick requires it, and the row names which), unticked, and teched (the full
// extent, where every row is in the set because Select all said so) — so the game's rule is
// visible on the rows themselves rather than surprising.

export type RowState = TechMark | "unticked";

/** A row's state: the frame's own mark on the key (lib/frame's techMarker, the one rule the
 * tree's cells read too), with the absence spelled out. Ticked, implied and teched all show a
 * checked box, because all three ARE researched in the set: the difference is why, and the row
 * says so. */
export function rowState(mark: TechMark | undefined): RowState {
  return mark ?? "unticked";
}

/** Why a row states its membership instead of offering a tick, or undefined when it offers
 * one. Exactly one row is stated: the full extent, where every box is checked because `all`
 * says so and there is no all-but-one to serialise (tech-set.ts), so Deselect all is the only
 * way out. Issue 39's silent rows were the second case and went with the rule that made them —
 * every subject has a row of its own now, so an implied row is an ordinary row and unticking
 * it cascades exactly as it always has. The words are the tree's own, so the two surfaces
 * explain one rule the same way. */
export function rowLockReason(state: RowState): string | undefined {
  return state === "teched" ? FULL_SET_NOTE : undefined;
}

export function TechPicker({
  panelId,
  frame,
  races,
  onChange,
  onClose,
}: {
  /** The panel's own DOM id, wired to its trigger's aria-controls. A comparison carries one
   * Tech control per column plus the bulk one (Issue 41), so the id cannot be a constant. */
  panelId: string;
  frame: StatFrame;
  /** The Races present on the page: one on an entity page, up to four on a table. */
  races: readonly Race[];
  onChange: (next: StatFrame) => void;
  onClose: () => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  // A comparison column's control lives INSIDE the table's horizontal scroller (Issue 41),
  // and the sticky label rail eats the first 150 px of it, so at 375 px a panel anchored to
  // column one's left edge opens 184 px in and hangs 160 px past the viewport. Nothing is
  // clipped — the scroller reaches all of it — but the reader would have to scroll the table
  // to read a row they just opened. Bringing the panel into view on open costs one scroll and
  // the panel paints over the rail (z-30 against z-10), so it lands fully visible. Then, on
  // every surface, the shell's viewport rule: a panel that still overhangs the window's right
  // edge (the /ships toolbar at 1280 px) is nudged back, as the Loadout picker's always was.
  useEffect(() => {
    const element = panel.current;
    if (element == null) return;
    const scroller = element.parentElement?.closest<HTMLElement>(".overflow-x-auto");
    if (scroller != null) {
      const box = element.getBoundingClientRect();
      const view = scroller.getBoundingClientRect();
      // Right edge into view, but never so far that the left edge leaves it.
      if (box.right > view.right) {
        scroller.scrollBy({ left: Math.min(box.left - view.left, box.right - view.right) });
      }
    }
    keepPanelOnScreen(element);
  }, []);
  const ticked = frame.tech;
  // The rows are every key the Races HAVE, so they do not depend on the set: a member with no
  // row is impossible, and the panel does not rebuild itself on every tick.
  const groups = useMemo(() => techGroupsFor(races), [races]);
  const mark = useMemo(() => techMarker(frame), [frame]);
  // Why each row the reader did not tick is in the set, in the game's own names.
  const why = useMemo(() => impliers(ticked ?? EMPTY_TECH_SET, researchIndex), [ticked]);

  function row(option: TechOption) {
    const key = option.key;
    return (
      <PickerRow
        key={key}
        option={option}
        state={rowState(mark(key))}
        impliedBy={why.get(key)}
        onToggle={(on) => onChange(toggleTech(frame, key, on))}
      />
    );
  }

  return (
    <PickerPanel
      panelRef={panel}
      panelId={panelId}
      width="w-[min(32rem,calc(100vw-1.5rem))]"
      summary={tickedCount(frame)}
      // The panel leads with the set's two extents (ADR 0011): Select all IS Fully-Teched
      // and Deselect all IS Base, so the retired segmented control's presets stay one
      // click each without being a second way to say the same thing.
      actions={
        <>
          <PanelAction onClick={() => onChange(selectAllTech(frame))}>Select all</PanelAction>
          <PanelAction onClick={() => onChange(deselectAllTech(frame))}>Deselect all</PanelAction>
          <PanelAction onClick={onClose}>Close</PanelAction>
        </>
      }
    >
      {groups.length === 0 ? (
        <p className="py-2 text-[13px] text-ink-3">Nothing on this page has research to tick.</p>
      ) : (
        groups.map((group) => (
          <section key={group.race} aria-label={RACE_META[group.race].label}>
            <h3
              className={clsx(
                "sticky top-0 z-10 bg-raised pt-2 pb-1 font-condensed text-[12px] font-semibold uppercase tracking-[0.14em]",
                RACE_META[group.race].textClass,
              )}
            >
              {RACE_META[group.race].label}
            </h3>
            {group.domains.map((domain) => (
              <section key={domain.domain} aria-label={domain.name}>
                <h4 className="pt-2 pb-1 font-condensed text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-2">
                  {domain.name}
                </h4>
                {domain.tiers.map((tier) => (
                  <ul key={tier.tier} className="flex flex-col border-l border-line-faint pl-2">
                    {row(tier.unlock)}
                    {tier.subjects.map(row)}
                  </ul>
                ))}
              </section>
            ))}
          </section>
        ))
      )}
    </PickerPanel>
  );
}

/** What the panel is holding, in the same three extents the trigger names. */
function tickedCount(frame: StatFrame): string {
  if (frame.tech !== undefined) return `${frame.tech.size} ticked`;
  return frame.research === "fullyTeched" ? "Everything ticked" : "Nothing ticked";
}

/** The four states, said in colour AND in words: a ticked row is lit, an implied row is
 * quieter and carries the name of what requires it, an unticked row is plain. Colour alone
 * would not tell a reader why a box they never clicked is checked. */
const ROW_CLASS: Record<RowState, string> = {
  ticked: "bg-surface",
  teched: "bg-surface",
  implied: "bg-surface/40",
  unticked: "",
};

function PickerRow({
  option,
  state,
  impliedBy,
  onToggle,
}: {
  option: TechOption;
  state: RowState;
  /** The key of the thing that requires this row, when the reader did not tick it. */
  impliedBy?: TechKey;
  onToggle: (on: boolean) => void;
}) {
  const tier = option.kind === "tier";
  const requirer = impliedBy === undefined ? undefined : techKeyLabel(impliedBy);
  // Some rows state their membership rather than offering a tick: the full extent, where `all`
  // is a value and not a listed membership, and a silent row something else requires. The
  // reason is the row's own title, so the box is never mysteriously inert.
  const locked = rowLockReason(state);
  const full = locked !== undefined;
  return (
    <li data-tech-key={option.key} data-tech-state={state}>
      <label
        title={locked}
        className={clsx(
          "flex items-start gap-2 rounded px-1 py-1",
          full ? "cursor-default" : "cursor-pointer hover:bg-surface",
          ROW_CLASS[state],
        )}
      >
        <input
          type="checkbox"
          checked={state !== "unticked"}
          disabled={full}
          onChange={(event) => onToggle(event.target.checked)}
          className={clsx(
            "mt-[3px] shrink-0 accent-link focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-link",
            full ? "cursor-default" : "cursor-pointer",
          )}
        />
        <ArtImage
          asset={option.art}
          alt=""
          className={clsx(
            "mt-px h-6 w-auto shrink-0 object-contain",
            state === "unticked" && "opacity-70",
          )}
        />
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span
              className={clsx(
                "min-w-0 break-words text-[13.5px]",
                tier ? "font-semibold text-ink-2" : "text-ink-3",
                state === "ticked" && "text-ink",
              )}
            >
              {option.label}
            </span>
            {option.faction !== undefined && (
              <Chip
                label={option.faction}
                title={`Only the ${option.faction} can research this`}
                className="shrink-0"
              />
            )}
            {option.field !== undefined && (
              <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-2">
                {option.field}
              </span>
            )}
          </span>
          {/* The Effect Lines are the same ones the tree shows (a Tier's are its Tier Bonuses). */}
          <RowFooter effects={option.effects}>
            <span>{priceParts(option.price).join(", ")}</span>
            {requirer !== undefined && <span>Required by {requirer}</span>}
          </RowFooter>
        </span>
      </label>
    </li>
  );
}
